import { useMemo } from 'react'
import { Box, capitalize, Chip, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useTheme } from '@mui/material/styles'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { CertificateRollupItem, CertificateRollupItemWithId } from '../types'
import CertificateDownloadCell from './certificate-download-cell'

dayjs.extend(utc)

interface CertificatesDataGridProps {
  data: CertificateRollupItem[]
  isLoading: boolean
  hasLinkedService: boolean
  typeFilter?: CertificateRollupItem['type'] | null
  categoryFilter?: string | null
  hasDateFilter?: boolean
}

function transformRollupData(
  data: CertificateRollupItem[],
): CertificateRollupItemWithId[] {
  return data.map((item, index) => ({
    ...item,
    id: `${item.certificateID || 'no-cert'}-${index}`,
  }))
}

function CustomToolbar() {
  return <DataGridToolbar fileName="certificates-learning-summary" />
}

export default function CertificatesDataGrid({
  data,
  isLoading,
  hasLinkedService,
  typeFilter = null,
  categoryFilter = null,
  hasDateFilter = false,
}: CertificatesDataGridProps) {
  let theme = useTheme()

  let transformedData = useMemo(() => {
    if (!data) return []
    let filtered = data
      .filter((item) => !typeFilter || item.type === typeFilter)
      .filter(
        (item) =>
          !categoryFilter ||
          (item.category || 'Uncategorized') === categoryFilter,
      )
    return transformRollupData(filtered)
  }, [data, typeFilter, categoryFilter])

  let baseColumns: GridColDef[] = [
    {
      field: 'courseActivity',
      headerName: 'Course/Activity',
      width: 300,
      sortable: true,
    },
    {
      field: 'certificateID',
      headerName: 'Certificate',
      width: data.some((i) => i.type === 'external') ? 280 : 140,
      sortable: false,
      renderCell: (params) => {
        let item = params.row as CertificateRollupItemWithId
        return <CertificateDownloadCell item={item} />
      },
      valueGetter: () => 'Download Certificate',
    },
    {
      field: 'completionDate',
      headerName: 'Completion Date',
      width: 150,
      type: 'date',
      sortable: true,
      renderCell: (params) => (
        <span>{dayjs.utc(params.value).format('MMM DD, YYYY')}</span>
      ),
      valueGetter: (params) => new Date(params),
    },
    {
      field: 'ceHours',
      headerName: 'CE Hours',
      width: 100,
      type: 'number',
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ color: theme.palette.entityColors.ceActivity }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: 'accreditationStatus',
      headerName: 'Accreditation Status',
      width: 150,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          variant="outlined"
          sx={{
            backgroundColor: 'transparent',
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        />
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 200,
      sortable: true,
      // Matches the 'Uncategorized' bucket used by the breakdown and
      // drill-down, and keeps sorting, filtering, and CSV export consistent.
      valueGetter: (value) => value || 'Uncategorized',
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={capitalize(params.value)}
          variant="outlined"
          sx={{
            backgroundColor: 'transparent',
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        />
      ),
    },
    {
      field: 'courseCode',
      headerName: 'Course Code',
      width: 120,
      sortable: true,
      renderCell: (params) => <span>{params.value || '-'}</span>,
    },
  ]

  let assignmentColumns: GridColDef[] = [
    {
      field: 'assignment',
      headerName: 'Was Assigned',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Yes' : 'No'}
          variant="outlined"
          sx={{
            backgroundColor: 'transparent',
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        />
      ),
    },
    {
      field: 'assignmentName',
      headerName: 'Assignment Name',
      width: 200,
      sortable: true,
      renderCell: (params) => <span>{params.value || '-'}</span>,
    },
  ]

  let columns = hasLinkedService
    ? [...baseColumns, ...assignmentColumns]
    : baseColumns

  if (!transformedData || transformedData.length === 0) {
    let isFilteredResult = Boolean(
      typeFilter || categoryFilter || hasDateFilter,
    )
    return (
      <Box
        sx={{
          height: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'text.secondary',
          backgroundColor: 'background.paper',
          borderRadius: 1,
        }}
        role="status"
        aria-live="polite"
      >
        <Box
          sx={{ fontSize: 64, mb: 2, opacity: 0.5 }}
          role="img"
          aria-label="No certificates icon"
        >
          📜
        </Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {isFilteredResult ? 'No Matching Records' : 'No Certificates Found'}
        </Typography>
        <Typography variant="body2">
          {isFilteredResult
            ? 'No records match the current filters. Try adjusting or clearing them to see more records.'
            : 'No learning achievements found. Complete courses to see your certificates here.'}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Typography id="certificates-table-description" className="sr-only">
        Table showing your earned certificates and completed learning
        activities. Use arrow keys to navigate, Enter to select rows, Space to
        toggle selection.
      </Typography>
      <DataGrid
        rows={transformedData}
        columns={columns}
        slots={{ toolbar: CustomToolbar }}
        showToolbar
        loading={isLoading}
        aria-label="Certificates and learning records table"
        aria-describedby="certificates-table-description"
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: {
            sortModel: [{ field: 'completionDate', sort: 'desc' }],
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        sx={{
          backgroundColor: 'background.paper',
          '& .MuiDataGrid-toolbarContainer': {
            borderBottom: 1,
            borderColor: 'divider',
            p: 2,
            backgroundColor: 'background.default',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.paperDark',
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: 'background.default',
            },
          },
        }}
      />
    </Box>
  )
}
